import { fetchData } from "./api";

/**
 * Assigns a user with the given email as the coach of the team.
 * Creates a player record if they don't exist, demotes any existing coach,
 * and sets the new coach's role in team_rosters.
 *
 * @param {string|number} teamId
 * @param {string} email
 * @returns {Promise<string|number>} The person_id of the assigned coach
 */
export const assignCoachToTeam = async (teamId, email) => {
  const trimmedEmail = email.trim().toLowerCase();
  if (!trimmedEmail) return null;

  let playerId = null;
  const existingPlayers = await fetchData(
    `/people?email=eq.${encodeURIComponent(trimmedEmail)}`,
  );

  if (existingPlayers?.length > 0) {
    playerId = existingPlayers[0].person_id;
  } else {
    const existingUsers = await fetchData(
      `/users?email=eq.${encodeURIComponent(trimmedEmail)}`,
    );
    const matchedUser = existingUsers?.length > 0 ? existingUsers[0] : null;

    const newPlayerPayload = {
      email: trimmedEmail,
      first_name: matchedUser?.first_name || "Coach",
      last_name: matchedUser?.last_name || "Player",
      user_id: matchedUser ? matchedUser.user_id : null,
    };

    const createdPlayers = await fetchData(`/people`, {
      method: "POST",
      headers: { Prefer: "return=representation" },
      body: JSON.stringify(newPlayerPayload),
    });

    if (createdPlayers?.length > 0) {
      playerId = createdPlayers[0].person_id;
    }
  }

  if (!playerId) {
    throw new Error("Could not initialize player record for coach.");
  }

  try {
    await fetchData(
      `/team_rosters?team_id=eq.${teamId}&role=in.(captain,coach)`,
      {
        method: "PATCH",
        body: JSON.stringify({ role: "player" }),
      },
    );
  } catch {
    // Ignore if no existing coach
  }

  const existingRoster = await fetchData(
    `/team_rosters?team_id=eq.${teamId}&person_id=eq.${playerId}`,
  );

  if (existingRoster?.length > 0) {
    await fetchData(
      `/team_rosters?team_id=eq.${teamId}&person_id=eq.${playerId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ role: "coach" }),
      },
    );
  } else {
    await fetchData(`/team_rosters`, {
      method: "POST",
      body: JSON.stringify({
        team_id: teamId,
        person_id: playerId,
        role: "coach",
      }),
    });
  }

  return playerId;
};

export const assignCaptainToTeam = assignCoachToTeam;
